import { useEffect, useState, useMemo } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import { RecordCommentsThread } from "@/components/app/RecordCommentsThread";
import { CheckCircle, X, Eye, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ApprovalsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quickAction, setQuickAction] = useState(null);  // {item, type}
    const [comment, setComment] = useState("");
    const [detail, setDetail] = useState(null);             // open form view

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/approvals/pending");
            setItems(data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const performQuick = async () => {
        try {
            const url = `/forms/${quickAction.item.form_id}/records/${quickAction.item.record_id}/${quickAction.type}`;
            await api.post(url, { comment: comment || null });
            toast.success(quickAction.type === "approve" ? "Approved" : "Rejected");
            setQuickAction(null); setComment(""); load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const openDetail = async (item) => {
        try {
            const [{ data: meta }, { data: rec }] = await Promise.all([
                api.get(`/forms/${item.form_id}`),
                api.get(`/forms/${item.form_id}/records/${item.record_id}`),
            ]);
            setDetail({ item, meta, record: rec, comment: "" });
        } catch (e) { toast.error(extractError(e)); }
    };

    const detailAction = async (type) => {
        try {
            await api.post(`/forms/${detail.item.form_id}/records/${detail.item.record_id}/${type}`, { comment: detail.comment || null });
            toast.success(type === "approve" ? "Approved" : "Rejected");
            setDetail(null); load();
        } catch (e) { toast.error(extractError(e)); }
    };

    return (
        <PageContainer>
            <PageHeader overline="Workflow" title="Pending approvals" description="Records waiting on your decision. Click View to see the full submission." testid="approvals-header" />

            {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
             items.length === 0 ? <EmptyState icon={CheckCircle} title="All clear" description="No approvals waiting on you." testid="approvals-empty" /> : (
                <div className="brut-section overflow-x-auto">
                    <table className="w-full" data-testid="approvals-table">
                        <thead><tr>
                            <th className="data-header text-left">Form</th>
                            <th className="data-header text-left">Submitted by</th>
                            <th className="data-header text-left">Current step</th>
                            <th className="data-header text-left">Preview</th>
                            <th className="data-header text-left">When</th>
                            <th className="data-header"></th>
                        </tr></thead>
                        <tbody>
                            {items.map((it) => {
                                const isSimple = (it.form_complexity || "simple") === "simple";
                                return (
                                    <tr key={it.record_id} className="hover:bg-secondary/50" data-testid={`approval-row-${it.record_id}`}>
                                        <td className="data-cell">
                                            <div className="font-medium">{it.form_name}</div>
                                            <div className="text-xs font-mono text-muted-foreground">{it.form_code}</div>
                                        </td>
                                        <td className="data-cell text-sm">{it.created_by?.name || it.created_by?.email || "—"}</td>
                                        <td className="data-cell"><Badge variant="outline" className="rounded-sm font-mono text-xs">Step {it.step_index + 1} · {it.step_name}</Badge></td>
                                        <td className="data-cell text-xs">
                                            <div className="space-y-0.5">
                                                {Object.entries(it.data_preview || {}).slice(0, 3).map(([k, v]) => (
                                                    <div key={k}><span className="font-mono text-muted-foreground">{k}:</span> {String(v ?? "—")}</div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="data-cell font-mono text-xs text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleString() : "—"}</td>
                                        <td className="data-cell text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openDetail(it)} data-testid={`view-${it.record_id}`}><Eye size={14} className="mr-1" /> View</Button>
                                                {isSimple && (
                                                    <>
                                                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { setQuickAction({ item: it, type: "reject" }); setComment(""); }} data-testid={`reject-${it.record_id}`}><X size={14} className="mr-1" /> Reject</Button>
                                                        <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => { setQuickAction({ item: it, type: "approve" }); setComment(""); }} data-testid={`approve-${it.record_id}`}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
             )
            }

            {/* Quick action (simple forms) */}
            <Dialog open={!!quickAction} onOpenChange={(v) => { if (!v) { setQuickAction(null); setComment(""); } }}>
                <DialogContent className="rounded-sm" data-testid="approval-dialog">
                    <DialogHeader><DialogTitle>{quickAction?.type === "approve" ? "Approve" : "Reject"} record</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm border border-border bg-secondary p-3 rounded-sm">
                            <div className="font-medium">{quickAction?.item?.form_name}</div>
                            <div className="text-xs text-muted-foreground mt-1">Step: {quickAction?.item?.step_name}</div>
                        </div>
                        <div>
                            <Label className="label-overline">Comment (optional)</Label>
                            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="mt-2 rounded-sm" placeholder="Reason / notes…" data-testid="approval-comment-input" />
                        </div>
                        <DialogFooter>
                            <Button onClick={performQuick} className={`rounded-sm ${quickAction?.type === "approve" ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "bg-destructive hover:bg-destructive/90 text-destructive-foreground"}`} data-testid="approval-confirm-btn">
                                Confirm {quickAction?.type}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Detail view (complex / medium / any "View") */}
            <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
                <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="approval-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 flex-wrap">
                            <span>{detail?.meta?.form?.form_name || "Form"}</span>
                            <Badge className="rounded-sm font-mono text-[10px] bg-warning text-foreground">Step {detail?.item?.step_index + 1} · {detail?.item?.step_name}</Badge>
                        </DialogTitle>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm border border-border surface rounded-sm p-4">
                                <div><div className="label-overline mb-1">Submitted by</div><div>{detail.item.created_by?.name || detail.item.created_by?.email || "—"}</div></div>
                                <div><div className="label-overline mb-1">Submitted</div><div className="font-mono text-xs">{detail.item.created_at ? new Date(detail.item.created_at).toLocaleString() : "—"}</div></div>
                                <div><div className="label-overline mb-1">Record ID</div><div className="font-mono text-xs truncate">{detail.item.record_id}</div></div>
                            </div>

                            <div>
                                <div className="label-overline mb-3">Form data</div>
                                <DynamicFormRenderer
                                    sections={detail.meta.sections}
                                    fields={detail.meta.fields}
                                    data={detail.record.data || {}}
                                    onChange={() => {}}
                                    readOnly
                                    layoutMode={detail.meta.form.layout_type || detail.meta.form.layout_mode || "single"}
                                    fieldControls={(() => {
                                        const steps = detail.meta.form.workflow_steps || [];
                                        const idx = detail.record.workflow_current_step ?? detail.item?.step_index ?? 0;
                                        const fc = steps[idx]?.field_controls || {};
                                        return Object.keys(fc).length ? fc : null;
                                    })()}
                                />
                            </div>

                            <RecordCommentsThread formId={detail.item.form_id} recordId={detail.item.record_id} />

                            {detail.record.workflow_history?.length > 0 && (
                                <div className="border-t border-border pt-5">
                                    <div className="label-overline mb-3 flex items-center gap-2"><ClockCounterClockwise size={12} /> History</div>
                                    <div className="space-y-2">
                                        {detail.record.workflow_history.map((h, i) => (
                                            <div key={`${h.at || ""}-${h.step ?? i}-${h.action || ""}`} className="flex items-start gap-3 text-sm border-l-2 border-border pl-3 py-1" data-testid={`history-${i}`}>
                                                <Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${h.action === "approve" ? "border-success text-success" : h.action === "reject" ? "border-destructive text-destructive" : ""}`}>{h.action}</Badge>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs">Step {h.step + 1}{h.step_name ? ` · ${h.step_name}` : ""}</div>
                                                    {h.comment && <div className="text-xs text-muted-foreground mt-0.5">"{h.comment}"</div>}
                                                </div>
                                                <div className="text-[11px] font-mono text-muted-foreground">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-border pt-5">
                                <Label className="label-overline mb-2 block">Decision comment (optional)</Label>
                                <Textarea value={detail.comment} onChange={(e) => setDetail({ ...detail, comment: e.target.value })} className="rounded-sm" placeholder="Reason / notes…" data-testid="detail-comment-input" />
                            </div>

                            <DialogFooter className="!justify-between flex-wrap gap-2">
                                <Button variant="outline" onClick={() => setDetail(null)} className="rounded-sm" data-testid="detail-close-btn">Close</Button>
                                <div className="flex gap-2">
                                    <Button onClick={() => detailAction("reject")} className="rounded-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="detail-reject-btn"><X size={14} className="mr-1" /> Reject</Button>
                                    <Button onClick={() => detailAction("approve")} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="detail-approve-btn"><CheckCircle size={14} className="mr-1" /> Approve</Button>
                                </div>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
